import { createContext, type JSX, type ReactNode, useContext } from 'react';
import { storage } from '@doxyme/ui/utils';
import { StorageKeys } from '../shared/constants';

/**
 * Holds the authenticated access token for the React tree.
 *
 * `null` (the default) means no provider is mounted — e.g. patient paths that
 * never mount Frontegg. Consumers fall back to storage in that case, which
 * removes their hidden dependence on a globally mounted Frontegg provider
 * (PROD-8068).
 */
export const AccessTokenContext = createContext<string | null>(null);

export const AccessTokenProvider = ({
  accessToken,
  children,
}: {
  accessToken: string | null;
  children: ReactNode;
}): JSX.Element => (
  <AccessTokenContext.Provider value={accessToken}>
    {children}
  </AccessTokenContext.Provider>
);

/**
 * Reads the access token from context, returning `null` when no provider is
 * mounted so callers can decide on a fallback.
 */
export const useAccessTokenContext = (): string | null =>
  useContext(AccessTokenContext);

/**
 * Resolves the access token from storage. Returns an empty string when the key
 * is absent. Kept here so the context and its consumers share one fallback
 * source of truth.
 */
export const resolveAccessTokenFromStorage = (): string => {
  const tokenFromStorage = storage.findItem(StorageKeys.AccessTokenId);
  return tokenFromStorage === undefined ? '' : `${tokenFromStorage}`;
};
